import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, User, Phone, MapPin, Car, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/shared/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { supabase } from '../lib/supabase';
import { getOficinaId } from '../lib/office';
import { maskCep, maskCpfCnpj, maskInternationalPhone, maskPhone, maskPostalCode, maskRegistration, onlyDigits } from '../utils/masks';
import {
  isValidCpf,
  isValidCnpj,
  isValidPhone,
  isValidEmail,
  isValidCep,
  isValidPlate
} from '../utils/validators';
import { getVehicleYearOptions } from '../utils/vehicleYears';
import { useLocalization } from '../hooks/useLocalization';

export default function NovoCliente() {
  const { settings } = useLocalization();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);

  const [formData, setFormData] = useState({
    // Etapa 1 - Dados pessoais
    nome: '',
    cpf: '',
    data_nascimento: '',

    // Etapa 2 - Contato
    telefone: '',
    email: '',

    // Etapa 3 - Endereço
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',

    // Etapa 4 - Veículos
    veiculos: [{
      placa: '',
      marca: '',
      marca_custom: '',
      modelo: '',
      ano: '',
      cor: '',
      km_atual: ''
    }]
  });

  const anos = getVehicleYearOptions();

  const steps = [
    { id: 1, title: 'Dados Pessoais', icon: User },
    { id: 2, title: 'Contato', icon: Phone },
    { id: 3, title: 'Endereço', icon: MapPin },
    { id: 4, title: 'Veículo', icon: Car }
  ];


  // Busca CEP
  const buscarCep = async () => {
    if (settings.countryCode !== 'BR') return;
    if (onlyDigits(formData.cep).length !== 8) {
      toast.error('CEP deve ter 8 dígitos');
      return;
    }

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${onlyDigits(formData.cep)}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      setFormData(prev => ({
        ...prev,
        rua: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || ''
      }));

      toast.success('Endereço encontrado!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // Adicionar veículo
  const adicionarVeiculo = () => {
    setFormData(prev => ({
      ...prev,
      veiculos: [...prev.veiculos, {
        placa: '',
        marca: '',
        marca_custom: '',
        modelo: '',
        ano: '',
        cor: '',
        km_atual: ''
      }]
    }));
  };

  const removerVeiculo = (index) => {
    if (formData.veiculos.length > 1) {
      setFormData(prev => ({
        ...prev,
        veiculos: prev.veiculos.filter((_, i) => i !== index)
      }));
    }
  };

  const atualizarVeiculo = (index, field, value) => {
    setFormData(prev => {
      const novosVeiculos = [...prev.veiculos];
      novosVeiculos[index][field] = value;
      return { ...prev, veiculos: novosVeiculos };
    });
  };

  // Validação
  const validarEtapa1 = () => {
    if (!formData.nome.trim()) {
      toast.error('Informe o nome do cliente.');
      return false;
    }

    if (!formData.cpf.trim()) {
      toast.error(`Informe o ${settings.registrationLabel}.`);
      return false;
    }

    if (settings.countryCode === 'BR') {
      const documentoValido =
        isValidCpf(formData.cpf) || isValidCnpj(formData.cpf);

      if (!documentoValido) {
        toast.error('Digite um CPF ou CNPJ válido.');
        return false;
      }
    }

    return true;
  };

  const validarEtapa2 = () => {
    if (!formData.telefone.trim()) {
      toast.error('Informe o telefone do cliente.');
      return false;
    }

    if (!isValidPhone(formData.telefone, settings.countryCode)) {
      toast.error('Informe um telefone válido.');
      return false;
    }

    if (!formData.email.trim()) {
      toast.error('Informe o e-mail do cliente.');
      return false;
    }

    if (!isValidEmail(formData.email)) {
      toast.error('Informe um e-mail válido.');
      return false;
    }

    return true;
  };

  const validarEtapa3 = () => {
    if (!formData.cep.trim()) {
      toast.error(
        settings.countryCode === 'BR'
          ? 'Informe o CEP.'
          : 'Informe o código postal.'
      );
      return false;
    }

    if (!isValidCep(formData.cep, settings.countryCode)) {
      toast.error(
        settings.countryCode === 'BR'
          ? 'Informe um CEP válido.'
          : 'Informe um código postal válido.'
      );
      return false;
    }

    if (!formData.numero.trim()) {
      toast.error('Informe o número do endereço.');
      return false;
    }

    return true;
  };

  const validarEtapa4 = () => {
    if (!formData.veiculos.length) {
      toast.error('Adicione pelo menos um veículo.');
      return false;
    }

    for (let i = 0; i < formData.veiculos.length; i += 1) {
      const veiculo = formData.veiculos[i];
      const numeroVeiculo = i + 1;

      const marcaFinal =
        veiculo.marca === '__other__'
          ? veiculo.marca_custom?.trim()
          : veiculo.marca?.trim();

      if (!veiculo.placa.trim()) {
        toast.error(`Informe a ${settings.vehicleRegistrationLabel} do veículo ${numeroVeiculo}.`);
        return false;
      }

      if (!isValidPlate(veiculo.placa, settings.countryCode)) {
        toast.error(
          settings.countryCode === 'BR'
            ? `Informe uma placa válida para o veículo ${numeroVeiculo}.`
            : `Informe um ${settings.vehicleRegistrationLabel} válido para o veículo ${numeroVeiculo}.`
        );
        return false;
      }

      if (!marcaFinal) {
        toast.error(`Informe a marca do veículo ${numeroVeiculo}.`);
        return false;
      }

      if (!veiculo.modelo.trim()) {
        toast.error(`Informe o modelo do veículo ${numeroVeiculo}.`);
        return false;
      }

      if (!veiculo.ano.trim()) {
        toast.error(`Selecione o ano do veículo ${numeroVeiculo}.`);
        return false;
      }

      if (!veiculo.cor.trim()) {
        toast.error(`Informe a cor do veículo ${numeroVeiculo}.`);
        return false;
      }
    }

    return true;
  };

  // Navegação
  const proximaEtapa = () => {
    let isValid = false;

    switch (currentStep) {
      case 1:
        isValid = validarEtapa1();
        break;
      case 2:
        isValid = validarEtapa2();
        break;
      case 3:
        isValid = validarEtapa3();
        break;
      case 4:
        isValid = validarEtapa4();
        break;
      default:
        isValid = false;
    }

    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const etapaAnterior = () => {
    setCurrentStep(currentStep - 1);
  };

  // Salvar tudo
  const salvarClienteEVeiculos = async () => {
    if (loading) return;
    if (!validarEtapa4()) return;

    setLoading(true);
    try {
      const oficinaId = await getOficinaId();

      if (!oficinaId) {
        toast.error('Oficina não encontrada para este usuário');
        return;
      }

      // Salvar cliente
      const { data: cliente, error: clienteError } = await supabase
        .from('clientes')
        .insert({
          nome: formData.nome.trim(),
          cpf: settings.countryCode === 'BR' ? formData.cpf.trim() : null,
          documento: formData.cpf.trim(),
          tipo_documento: settings.registrationLabel,
          pais_codigo: settings.countryCode,
          data_nascimento: formData.data_nascimento || null,
          telefone: formData.telefone.trim(),
          email: formData.email.trim(),
          endereco: `${formData.rua}, ${formData.numero} ${formData.complemento}`.trim(),
          bairro: formData.bairro.trim(),
          cidade: formData.cidade.trim(),
          estado: formData.estado.trim(),
          codigo_postal: formData.cep.trim(),
          oficina_id: oficinaId
        })
        .select()
        .single();

      if (clienteError) throw clienteError;

      // Salvar veículos
      const veiculosParaSalvar = formData.veiculos.map(v => ({
        placa: v.placa.trim(),
        marca: v.marca === '__other__'
          ? v.marca_custom.trim()
          : v.marca.trim(),
        modelo: v.modelo.trim(),
        cor: v.cor.trim(),
        cliente_id: cliente.id,
        oficina_id: oficinaId,
        ano: parseInt(v.ano, 10) || null,
        km_atual: parseInt(v.km_atual, 10) || null,
        odometro_unidade:
          settings.measurementSystem === 'imperial' ? 'mi' : 'km'
      }));

      if (veiculosParaSalvar.length > 0) {
        const { error: veiculosError } = await supabase
          .from('veiculos')
          .insert(veiculosParaSalvar);

        if (veiculosError) throw veiculosError;
      }

      toast.success('Cliente e veículos cadastrados com sucesso!');
      navigate('/clientes');
    } catch (error) {
      console.error('Erro ao salvar cliente e veículos:', error);
      toast.error(
        'Não foi possível concluir o cadastro. Verifique os dados e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const marcas = [
    'Fiat', 'Volkswagen', 'Chevrolet', 'Ford', 'Toyota',
    'Honda', 'Hyundai', 'Renault', 'Nissan', 'Jeep'
  ];

  const coresVeiculo = [
    'Branco', 'Preto', 'Prata', 'Cinza', 'Vermelho',
    'Azul', 'Verde', 'Amarelo', 'Rosa', 'Outra'
  ];


  return (
    <div className="space-y-6">
      <PageHeader
        title="Novo Cliente"
        subtitle="Cadastro completo com dados pessoais, contato, endereço e veículos"
      />

      {/* Barra de progresso */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center gap-2">
                <div
                  className={`h-2 w-8 rounded-full transition-colors ${step.id <= currentStep ? 'bg-accent' : 'bg-surface2'
                    }`}
                />
                {step.id < currentStep && (
                  <Check className="h-4 w-4 text-green-500" />
                )}
                {step.id === currentStep && (
                  <ChevronRight className="h-4 w-4 text-accent animate-pulse" />
                )}
              </div>
            ))}
          </div>
          <span className="text-sm text-zinc-400">
            Etapa {currentStep} de 4
          </span>
        </div>

        {/* Etapa 1 - Dados pessoais */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User size={20} className="text-accent" />
              Dados Pessoais
            </h3>

            <div>
              <label className="mb-1 block text-sm font-medium">Nome completo *</label>
              <Input
                placeholder="Nome completo do cliente"
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">{settings.registrationLabel} *</label>
                <Input
                  mask={settings.countryCode === 'BR' ? 'cpfCnpj' : 'registration'}
                  placeholder={settings.registrationLabel}
                  value={formData.cpf}
                  onChange={(e) => setFormData(prev => ({ ...prev, cpf: settings.countryCode === 'BR' ? maskCpfCnpj(e.target.value) : maskRegistration(e.target.value) }))}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Data de nascimento</label>
                <Input
                  type="date"
                  value={formData.data_nascimento}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_nascimento: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Etapa 2 - Contato */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Phone size={20} className="text-accent" />
              Dados de Contato
            </h3>

            <div>
              <label className="mb-1 block text-sm font-medium">Telefone *</label>
              <Input
                placeholder={settings.countryCode === 'BR' ? '(00) 00000-0000' : '+1 555 123 4567'}
                mask={settings.countryCode === 'BR' ? 'phone' : 'internationalPhone'}
                value={formData.telefone}
                onChange={(e) => setFormData(prev => ({ ...prev, telefone: settings.countryCode === 'BR' ? maskPhone(e.target.value) : maskInternationalPhone(e.target.value) }))}
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Email</label>
              <Input
                type="email"
                placeholder="cliente@email.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                disabled={loading}
              />
            </div>
          </div>
        )}

        {/* Etapa 3 - Endereço */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-accent" />
              Endereço
            </h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">{settings.postalCodeLabel} *</label>
                <div className="flex gap-2">
                  <Input
                    mask={settings.countryCode === 'BR' ? 'cep' : 'postalCode'}
                    placeholder={settings.postalCodeLabel}
                    value={formData.cep}
                    onChange={(e) => setFormData(prev => ({ ...prev, cep: settings.countryCode === 'BR' ? maskCep(e.target.value) : maskPostalCode(e.target.value) }))}
                    disabled={loading || loadingCep}
                  />
                  <Button
                    onClick={buscarCep}
                    disabled={loading || loadingCep || settings.countryCode !== 'BR' || onlyDigits(formData.cep).length !== 8}
                    className="whitespace-nowrap"
                  >
                    {loadingCep ? 'Buscando...' : 'Buscar'}
                  </Button>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Número *</label>
                <Input
                  mask="integer"
                  placeholder="Ex: 120"
                  value={formData.numero}
                  onChange={(e) => setFormData(prev => ({ ...prev, numero: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Endereço</label>
              <Input
                placeholder="Ex: Rua das Oficinas"
                value={formData.rua}
                onChange={(e) => setFormData(prev => ({ ...prev, rua: e.target.value }))}
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">Bairro</label>
                <Input
                  placeholder="Ex: Centro"
                  value={formData.bairro}
                  onChange={(e) => setFormData(prev => ({ ...prev, bairro: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Cidade</label>
                <Input
                  placeholder="Ex: Sao Paulo"
                  value={formData.cidade}
                  onChange={(e) => setFormData(prev => ({ ...prev, cidade: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">Estado</label>
                <Input
                  placeholder="Ex: SP"
                  value={formData.estado}
                  onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value }))}
                  maxLength={2}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Complemento</label>
                <Input
                  placeholder="Ex: Sala 2, fundos, bloco B"
                  value={formData.complemento}
                  onChange={(e) => setFormData(prev => ({ ...prev, complemento: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Etapa 4 - Veículos */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Car size={20} className="text-accent" />
              Veículos *
            </h3>
            <p className="text-sm text-zinc-400 mb-4">
              Pelo menos um veículo é obrigatório. Adicione quantos precisar.
            </p>

            {formData.veiculos.map((veiculo, index) => (
              <Card key={index} className="border border-border p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Veículo {index + 1}</h4>
                  {formData.veiculos.length > 1 && (
                    <Button
                      variant="ghost"
                      onClick={() => removerVeiculo(index)}
                      disabled={loading}
                      className="text-red-400 hover:text-red-300"
                    >
                      Remover
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium">{settings.vehicleRegistrationLabel} *</label>
                    <Input
                      mask="plate"
                      placeholder={settings.vehicleRegistrationLabel}
                      value={veiculo.placa}
                      onChange={(e) => atualizarVeiculo(index, 'placa', e.target.value)}
                      maxLength={15}
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Marca *</label>
                    <Select
                      value={veiculo.marca}
                      onChange={(e) => {
                        atualizarVeiculo(index, 'marca', e.target.value);
                        if (e.target.value !== '__other__') atualizarVeiculo(index, 'marca_custom', '');
                      }}
                      disabled={loading}
                    >
                      <option value="">Selecione...</option>
                      {marcas.map(marca => (
                        <option key={marca} value={marca}>{marca}</option>
                      ))}
                      <option value="__other__">Outra marca</option>
                    </Select>
                  </div>
                  {veiculo.marca === '__other__' && (
                    <div>
                      <label className="mb-1 block text-sm font-medium">Qual marca? *</label>
                      <Input
                        placeholder="Digite a marca do veículo"
                        value={veiculo.marca_custom || ''}
                        onChange={(e) => atualizarVeiculo(index, 'marca_custom', e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Modelo *</label>
                    <Input
                      placeholder="Ex: Gol, Onix, Corolla"
                      value={veiculo.modelo}
                      onChange={(e) => atualizarVeiculo(index, 'modelo', e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Ano *</label>
                    <Select
                      value={veiculo.ano}
                      onChange={(e) => atualizarVeiculo(index, 'ano', e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Selecione...</option>
                      {anos.map(ano => (
                        <option key={ano} value={ano}>{ano}</option>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Cor *</label>
                    <Select
                      value={veiculo.cor}
                      onChange={(e) => atualizarVeiculo(index, 'cor', e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Selecione...</option>
                      {coresVeiculo.map(cor => (
                        <option key={cor} value={cor}>{cor}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Odometro atual ({settings.measurementSystem === 'imperial' ? 'mi' : 'km'})</label>
                    <Input
                      mask="integer"
                      placeholder="Ex: 45000"
                      value={veiculo.km_atual}
                      onChange={(e) => atualizarVeiculo(index, 'km_atual', e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
              </Card>
            ))}

            <Button
              onClick={adicionarVeiculo}
              disabled={loading}
              className="w-full"
            >
              Adicionar Veículo
            </Button>
          </div>
        )}

        {/* Botões de navegação */}
        <div className="flex justify-between pt-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={currentStep > 1 ? etapaAnterior : () => navigate('/clientes')}
            disabled={loading}
          >
            <ChevronLeft size={16} className="mr-2" />
            {currentStep > 1 ? 'Anterior' : 'Cancelar'}
          </Button>

          <div className="flex gap-2">
            {currentStep < 4 && (
              <Button onClick={proximaEtapa} disabled={loading}>
                Próxima Etapa
                <ChevronRight size={16} className="ml-2" />
              </Button>
            )}

            {currentStep === 4 && (
              <Button onClick={salvarClienteEVeiculos} disabled={loading}>
                {loading ? 'Salvando...' : 'Concluir Cadastro'}
                <Check size={16} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

